import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, ShieldCheck, TriangleAlert } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button, Input, Logo } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { StagingBanner } from "@/components/StagingBanner";
import { afterLoginPath } from "@/lib/shop-claim";

import { t, msg } from "@/lib/i18n";
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Webmail-Direktlinks: ein Klick vom "Link ist unterwegs" ins Postfach */
function inboxFor(email: string): { label: string; url: string } | null {
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  if (/^(gmail|googlemail)\.com$/.test(domain)) return { label: msg("Gmail öffnen"), url: "https://mail.google.com/mail/u/0/#search/from%3Alogiful" };
  if (/^(outlook|hotmail|live)\.(com|de)$/.test(domain)) return { label: msg("Outlook öffnen"), url: "https://outlook.live.com/mail/" };
  if (/^(gmx)\.(de|net|at|ch)$/.test(domain)) return { label: msg("GMX öffnen"), url: "https://www.gmx.net/" };
  if (/^web\.de$/.test(domain)) return { label: msg("WEB.DE öffnen"), url: "https://web.de/" };
  return null;
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <>
    <StagingBanner />
    <div className="min-h-screen grid lg:grid-cols-[1fr_1fr]">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Logo />
        <div className="flex-1 grid place-items-center py-10">
          <div className="w-full max-w-sm animate-fade-in">{children}</div>
        </div>
        <p className="text-[12px] text-fg-subtle">© {new Date().getFullYear()} {t("Logiful ·")} <a className="hover:text-fg" href="https://logiful.de/impressum">{t("Impressum")}</a> · <a className="hover:text-fg" href="https://logiful.de/datenschutz">{t("Datenschutz")}</a></p>
      </div>
      {/* Markenfläche: ruhig, mit Nutzenversprechen statt Stockfoto */}
      <div className="hidden lg:flex flex-col justify-end p-12 bg-ink text-ink-fg relative overflow-hidden">
        <div aria-hidden className="absolute -top-24 -right-24 size-[420px] rounded-full bg-coral/20 blur-3xl" />
        <div aria-hidden className="absolute bottom-40 -left-20 size-[260px] rounded-full bg-coral/10 blur-3xl" />
        <div className="relative max-w-md">
          <p className="text-[28px] font-semibold leading-tight tracking-tight">{t("Dein Lager. Live. Ohne Rätselraten.")}</p>
          <ul className="mt-6 space-y-3 text-[14px] text-ink-fg/75">
            {[msg("Aufträge, Bestände und Tracking in Echtzeit"), msg("Stammdaten geprüft, bevor sie ins Lager gehen"), msg("Wareneingänge in einer Minute angemeldet")].map((line) => (
              <li key={line} className="flex items-center gap-2.5"><CheckCircle2 className="size-4 text-coral" /> {t(line)}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
    </>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem("lc-last-email") ?? "";
    } catch {
      return "";
    }
  });
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(params.get("error") === "expired" ? "Der Link ist abgelaufen oder wurde bereits verwendet. Fordere einfach einen neuen an." : null);
  const [cooldown, setCooldown] = useState(0);
  const valid = EMAIL.test(email.trim());

  // Schon angemeldet (z. B. Link in einem anderen Tab geöffnet)? Dann direkt ins Portal – geprüft beim Öffnen,
  // bei jeder Rückkehr zum Tab und, während auf den Link gewartet wird, alle 3 Sekunden.
  const navigate = useNavigate();
  const session = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, refetchOnWindowFocus: true, refetchInterval: state === "sent" ? 3000 : false });
  useEffect(() => {
    if (!session.data) return;
    const next = params.get("next");
    navigate(next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/login") ? next : afterLoginPath(), { replace: true });
  }, [session.data, params, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    if (!valid || state === "sending") return;
    setState("sending");
    setError(null);
    try {
      await api.auth.requestLink(email.trim());
      try {
        localStorage.setItem("lc-last-email", email.trim());
      } catch {
        /* egal */
      }
      setState("sent");
      setCooldown(30);
    } catch (e) {
      setState("idle");
      const body = e instanceof ApiError ? (e.body as { error?: string; message?: string }) : undefined;
      setError(
        e instanceof ApiError && e.status === 429
          ? "Zu viele Versuche. Bitte warte ein paar Minuten."
          : body?.error === "mail_failed"
            ? "Die E-Mail konnte gerade nicht versendet werden. Bitte versuche es in ein paar Minuten erneut – wir sind informiert."
          : body?.error === "mail_not_configured"
            ? "Der E-Mail-Versand ist noch nicht eingerichtet. Bitte wende dich an Logiful."
            : "Das hat nicht geklappt. Bitte versuche es erneut.",
      );
    }
  };

  if (state === "sent") {
    const inbox = inboxFor(email);
    return (
      <Shell>
        <div className="size-12 rounded-2xl bg-coral-soft grid place-items-center"><Mail className="size-6 text-coral" /></div>
        <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{t("Schau in dein Postfach")}</h1>
        <p className="mt-2 text-fg-muted">
          {t("Wenn")} <span className="font-medium text-fg">{email.trim()}</span> {t("bei uns registriert ist, ist ein Anmeldelink unterwegs. Er ist 15 Minuten gültig.")}
        </p>
        <div className="mt-6 space-y-2">
          {inbox && (
            <a href={inbox.url} target="_blank" rel="noreferrer" className="w-full">
              <Button variant="primary" className="w-full h-10">{t(inbox.label)} <ArrowRight className="size-4" /></Button>
            </a>
          )}
          <Button className="w-full h-10" disabled={cooldown > 0} onClick={() => void send()}>
            {cooldown > 0 ? t("Erneut senden in {cooldown} s", { cooldown }) : t("Link erneut senden")}
          </Button>
        </div>
        <p className="mt-6 text-[12.5px] text-fg-subtle">{t("Nichts angekommen? Prüfe den Spam-Ordner oder ob die Adresse stimmt.")}</p>
        <button onClick={() => setState("idle")} className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-fg-muted hover:text-fg">
          <ArrowLeft className="size-3.5" /> {t("Andere E-Mail-Adresse")}
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-[26px] font-semibold tracking-tight">{t("Anmelden")}</h1>
      <p className="mt-1.5 text-fg-muted">{t("Ohne Passwort – wir schicken dir einen Link per E-Mail.")}</p>
      <form className="mt-8 space-y-3" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <label className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">{t("Geschäftliche E-Mail-Adresse")}</span>
          <Input autoFocus type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("name@firma.de")} className="mt-1 h-10 text-[14px]" invalid={!!error} />
        </label>
        {error && <p className="flex items-start gap-1.5 text-[12.5px] text-danger"><TriangleAlert className="size-3.5 mt-0.5 shrink-0" /> {error}</p>}
        <Button type="submit" variant="primary" className="w-full h-10 text-[14px]" disabled={!valid} loading={state === "sending"}>
          {t("Anmeldelink senden")}
        </Button>
      </form>
      <p className="mt-6 flex items-center gap-1.5 text-[12px] text-fg-subtle"><ShieldCheck className="size-3.5" /> {t("Noch kein Zugang? Dein Administrator lädt dich ein.")}</p>
      <p className="mt-2 text-[13px] text-fg-muted">{t("Neu bei Logiful?")} <Link to="/registrieren" className="underline hover:text-fg">{t("Konto anlegen")}</Link></p>
    </Shell>
  );
}

/** Bestätigungsseite: Erst der Klick löst den Link ein – E-Mail-Scanner öffnen Links, klicken aber keine Buttons */
export function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<"ready" | "busy" | "error">(token ? "ready" : "error");

  const confirm = async () => {
    setState("busy");
    try {
      const r = await api.auth.verify(token);
      await qc.invalidateQueries({ queryKey: ["me"] });
      // Zwei-Faktor eingerichtet → Code abfragen; vorgeschrieben, aber nicht eingerichtet → Portal zeigt die Einrichtung
      navigate(r.mfa === "pending" ? "/login/mfa" : afterLoginPath(), { replace: true });
    } catch {
      setState("error");
    }
  };

  return (
    <Shell>
      {state === "error" ? (
        <>
          <div className="size-12 rounded-2xl bg-danger-soft grid place-items-center"><TriangleAlert className="size-6 text-danger" /></div>
          <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{t("Link nicht mehr gültig")}</h1>
          <p className="mt-2 text-fg-muted">{t("Anmeldelinks gelten 15 Minuten und nur einmal. Fordere einfach einen neuen an.")}</p>
          <Link to="/login"><Button variant="primary" className="mt-6 w-full h-10">{t("Neuen Link anfordern")}</Button></Link>
        </>
      ) : (
        <>
          <h1 className="text-[26px] font-semibold tracking-tight">{t("Fast geschafft")}</h1>
          <p className="mt-1.5 text-fg-muted">{t("Bestätige, dass du dich auf diesem Gerät anmelden möchtest.")}</p>
          <Button autoFocus variant="primary" className="mt-8 w-full h-10 text-[14px]" loading={state === "busy"} onClick={() => void confirm()}>
            {t("Jetzt anmelden")} <ArrowRight className="size-4" />
          </Button>
          <p className="mt-4 text-[12px] text-fg-subtle">{t("Du bleibst 14 Tage angemeldet. Auf fremden Geräten bitte nach der Arbeit abmelden.")}</p>
        </>
      )}
    </Shell>
  );
}

/** Zweiter Faktor nach dem E-Mail-Link: Code aus der Authenticator-App oder Wiederherstellungscode */
export function MfaPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [state, setState] = useState<"ready" | "busy">("ready");
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setState("busy");
    setError(null);
    try {
      const r = await api.auth.mfa(code);
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate(afterLoginPath(), { replace: true, state: r.usedRecovery ? { usedRecovery: true } : undefined });
    } catch (e) {
      setState("ready");
      if (e instanceof ApiError && e.status === 401) return navigate("/login", { replace: true });
      setError(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Der Code stimmt nicht.") : "Verbindung fehlgeschlagen – bitte erneut versuchen.");
    }
  };
  const valid = recovery ? code.replace(/[\s-]/g, "").length >= 8 : code.replace(/\s/g, "").length === 6;
  return (
    <Shell>
      <div className="size-12 rounded-2xl bg-surface-2 grid place-items-center"><ShieldCheck className="size-6" /></div>
      <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{t("Bestätigungscode")}</h1>
      <p className="mt-1.5 text-fg-muted">{recovery ? t("Einen deiner Wiederherstellungscodes eingeben – jeder gilt nur einmal.") : t("Gib den 6-stelligen Code aus deiner Authenticator-App ein.")}</p>
      <form className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); if (valid) void submit(); }}>
        <Input
          value={code}
          onChange={(e) => setCode(recovery ? e.target.value.slice(0, 12) : e.target.value.replace(/[^\d ]/g, "").slice(0, 7))}
          placeholder={recovery ? t("xxxx-xxxx") : "123 456"}
          inputMode={recovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          autoFocus
          aria-label={recovery ? t("Wiederherstellungscode") : t("6-stelliger Code")}
          className="h-12 text-[20px] tracking-[0.25em] font-mono"
          invalid={!!error}
        />
        {error && <p className="text-[12.5px] text-danger">{error}</p>}
        <Button variant="primary" className="w-full h-10 text-[14px]" loading={state === "busy"} disabled={!valid}>{t("Anmelden")} <ArrowRight className="size-4" /></Button>
      </form>
      <button className="mt-4 text-[12.5px] text-fg-muted hover:text-fg underline underline-offset-2" onClick={() => { setRecovery((r) => !r); setCode(""); setError(null); }}>
        {recovery ? t("Doch den App-Code verwenden") : t("Kein Zugriff auf die App? Wiederherstellungscode verwenden")}
      </button>
      <p className="mt-6 text-[12px] text-fg-subtle">{t("Weder App noch Codes? Ein Administrator eures Teams (oder Logiful) kann die Zwei-Faktor-Anmeldung zurücksetzen.")}</p>
    </Shell>
  );
}
